import { type MouseEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { ArrowUpRight, Link as LinkIcon } from 'lucide-react';
import { footerConfig as defaultFooterConfig, bookConfig as defaultBookConfig } from '@/config';
import type { LegalConfig } from '@/config';
import { legalConfig as defaultLegalConfig } from '@/config-defaults';
import { getIcon } from '@/lib/icons';
import { useConfig, useCollection } from '@/lib/siteData';
import { FooterNewsletter } from '@/components/FooterNewsletter';
import { imageVariants, withWebp } from '@/lib/imageSrc';

// Treat empty, "#", or whitespace-only hrefs as placeholders and skip rendering
// them — both for socials (CMS-managed) and footer columns (config-driven).
function isLiveHref(href: string | undefined | null): href is string {
  if (!href) return false;
  const trimmed = href.trim();
  return trimmed.length > 0 && trimmed !== '#';
}

// Admin tooling is not advertised to anonymous visitors. Strip any /admin link
// that finds its way into a footer column (the CMS lets editors type anything;
// this is a defensive belt to a server-side scrub that runs at boot).
function isPublicHref(href: string): boolean {
  const trimmed = href.trim().toLowerCase();
  if (trimmed === '/admin') return false;
  if (trimmed.startsWith('/admin/')) return false;
  if (trimmed.startsWith('/admin?')) return false;
  if (trimmed.startsWith('/admin#')) return false;
  return true;
}

// Defensive scrub: strip any `bcc=` query param from mailto: hrefs so a stale
// DB value can never leak a personal BCC address into the public page source.
// Repo defaults carry no bcc, but footer links are admin-editable.
function stripMailtoBcc(href: string): string {
  if (!/^mailto:/i.test(href.trim())) return href;
  const [base, query] = href.split('?');
  if (!query) return href;
  const kept = query.split('&').filter((p) => !/^bcc=/i.test(p));
  return kept.length > 0 ? `${base}?${kept.join('&')}` : base;
}

type ResolvedSocial = { key: string; label: string; href: string; iconName: string };

interface SocialLinkRow {
  id: string;
  label: string;
  href: string;
  iconName: string;
  sortOrder: number;
  visible: boolean;
}

export function Footer() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });
  const location = useLocation();
  // Home already renders the CTA section's inline newsletter form directly
  // above the footer — two stacked subscribe forms read as a glitch, so the
  // footer one is suppressed on `/` only.
  const isHome = location.pathname === '/';
  const footerConfig = useConfig('footer_config', defaultFooterConfig);
  const bookConfig = useConfig('book_config', defaultBookConfig);
  const legalConfig = useConfig<LegalConfig>('legal_config', defaultLegalConfig);
  const { data: apiSocial } = useCollection<SocialLinkRow>('social-links');
  const privacyPublished = legalConfig.visible === true;
  const bookEnabled = Boolean(bookConfig.enabled) && Boolean(bookConfig.coverImage);
  // Cover sits in a footer column, ~300px at lg. Covers are usually admin
  // uploads, so this is null most of the time — the plain <img> then renders.
  const bookCoverVariants = imageVariants(bookConfig.coverImage, '(min-width: 1024px) 300px, 100vw');
  // Footer hides the social-icons row when the admin toggles it off in
  // /admin/footer-config. Defaults to true when the field is absent.
  const showSocialRow = footerConfig.showSocialRow !== false;

  // Social icons sourced from the global social_links table (managed via
  // /admin/social). The legacy footer_config.socialLinks array was migrated
  // into this table by the admin-consolidation v1 migrator and is no longer
  // a fallback path.
  const socialLinks: ResolvedSocial[] = showSocialRow
    ? apiSocial
        .filter((l) => l.visible && isLiveHref(l.href))
        .map((l) => ({ key: l.id, label: l.label, href: l.href, iconName: l.iconName }))
    : [];

  if (
    !footerConfig.logo &&
    footerConfig.columns.length === 0 &&
    socialLinks.length === 0
  ) {
    return null;
  }

  const handleNavClick = (e: MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href.startsWith('#')) {
      e.preventDefault();
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <footer
      id="site-footer"
      data-newsletter-anchor
      ref={ref}
      className="w-full bg-surface-dark text-white py-16 lg:py-24"
    >
      <div className="container-large px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8">
          {/* Brand Column */}
          <div
            className={cn(
              'lg:col-span-4 space-y-6 transition-all duration-800 ease-out-quart',
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            )}
          >
            {footerConfig.logo && (
              <Link to="/" className="inline-block rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40" aria-label="Home">
                <span className="text-2xl font-semibold tracking-tight">{footerConfig.logo}</span>
              </Link>
            )}
            {footerConfig.description && (
              <p className="text-sm text-white/60 max-w-xs leading-relaxed">
                {footerConfig.description}
              </p>
            )}

            {/* Social Links (CMS-managed, falls back to config) */}
            {socialLinks.length > 0 && (
              <div className="flex flex-wrap gap-3 pt-2">
                {socialLinks.map((social) => {
                  const Icon = getIcon(social.iconName, LinkIcon);
                  return (
                    <a
                      key={social.key}
                      href={social.href}
                      target={social.href.startsWith('http') ? '_blank' : undefined}
                      rel={social.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                      className="w-10 h-10 border border-white/20 rounded-full flex items-center justify-center hover:bg-white hover:text-surface-dark transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                      aria-label={social.label}
                    >
                      <Icon className="w-4 h-4" />
                    </a>
                  );
                })}
              </div>
            )}

            {/* Newsletter signup (ListMonk) — self-hides when disabled; also
                suppressed on Home where the CTA section renders its own form */}
            {!isHome && <FooterNewsletter />}
          </div>

          {/* Links Columns */}
          {footerConfig.columns
            .map((column) => ({
              ...column,
              links: column.links
                .filter((l) => isLiveHref(l.href) && isPublicHref(l.href))
                .map((l) => ({ ...l, href: stripMailtoBcc(l.href) })),
            }))
            .filter((column) => column.links.length > 0)
            .map((column, colIndex) => (
            <div
              key={column.title}
              className={cn(
                'lg:col-span-2 transition-all duration-800 ease-out-quart',
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              )}
              style={{ transitionDelay: `${(colIndex + 1) * 100}ms` }}
            >
              {/* Styled as a heading but rendered as <p>: the footer columns
                  sit under no h2/h3 ancestor, so real h4s create a heading-
                  hierarchy skip flagged by a11y audits. */}
              <p className="text-xs font-geist-mono uppercase tracking-widest text-white/55 mb-4">
                {column.title}
              </p>
              <ul className="space-y-3">
                {column.links.map((link) => {
                  const isExternal = link.href.startsWith('http');
                  const isHash = link.href.startsWith('#');
                  const isInternal = !isExternal && !isHash;
                  const linkClass =
                    'text-sm text-white/70 hover:text-white transition-colors inline-flex items-center gap-1 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40';
                  const arrow = (
                    <ArrowUpRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200" />
                  );
                  return (
                    <li key={link.label}>
                      {isInternal ? (
                        <Link to={link.href} className={linkClass}>
                          {link.label}
                          {arrow}
                        </Link>
                      ) : (
                        <a
                          href={link.href}
                          onClick={(e) => handleNavClick(e, link.href)}
                          target={isExternal ? '_blank' : undefined}
                          rel={isExternal ? 'noopener noreferrer' : undefined}
                          className={linkClass}
                        >
                          {link.label}
                          {arrow}
                        </a>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

          {/* Book cover — replaces the old newsletter column */}
          <div
            className={cn(
              'lg:col-span-2 transition-all duration-800 ease-out-quart',
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            )}
            style={{ transitionDelay: '400ms' }}
          >
            {bookEnabled ? (
              <>
                <p className="text-xs font-geist-mono uppercase tracking-widest text-white/55 mb-4">
                  Forthcoming Book
                </p>
                <div className="overflow-hidden rounded-lg border border-white/10 shadow-lg bg-white/5">
                  {withWebp(
                    bookCoverVariants,
                    <img
                      src={bookConfig.coverImage}
                      alt={bookConfig.title || 'Forthcoming book'}
                      loading="eager"
                      decoding="async"
                      width={320}
                      height={480}
                      className="w-full h-auto object-cover"
                    />,
                  )}
                </div>
                {bookConfig.description && (
                  <p className="mt-3 text-xs text-white/50 leading-relaxed">
                    {bookConfig.description}
                  </p>
                )}
                {bookConfig.ctaLabel && bookConfig.ctaHref && (
                  <a
                    href={bookConfig.ctaHref}
                    className="mt-3 inline-flex items-center gap-1 text-xs text-white/70 hover:text-white underline rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                  >
                    {bookConfig.ctaLabel}
                    <ArrowUpRight className="w-3 h-3" />
                  </a>
                )}
              </>
            ) : null}
          </div>
        </div>

        {/* Bottom Bar */}
        {(footerConfig.copyright || footerConfig.credit || privacyPublished) && (
          <div
            className={cn(
              'mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4 transition-all duration-800 ease-out-quart',
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            )}
            style={{ transitionDelay: '500ms' }}
          >
            {footerConfig.copyright && (
              <p className="text-xs text-white/55">{footerConfig.copyright}</p>
            )}
            <div className="flex items-center gap-4">
              {privacyPublished && (
                <Link to="/privacy" className="text-xs text-white/55 hover:text-white transition-colors rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40">
                  Privacy
                </Link>
              )}
              {footerConfig.credit && (
                <p className="text-xs text-white/55">{footerConfig.credit}</p>
              )}
            </div>
          </div>
        )}
      </div>
    </footer>
  );
}
